import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CheckoutState } from "./checkout.state";
import {
  getCheckout,
  createCheckout,
  getCheckoutSession,
  createAddress,
  updateAddress,
  updateShippingAddress,
  updateBillingAddress,
  getInstallmentOptions,
} from "./checkout.thunk";
import {
  GetCheckoutSessionResponse,
  CreateAddressResponse,
  UpdateAddressResponse,
  UpdateShippingAddressResponse,
  UpdateBillingAddressResponse,
  GetInstallmentOptionsResponse,
  CheckoutSummaryItem,
} from "./checkout.types";

const initialState: CheckoutState = {
  data: null,
  status: "idle",
  error: null,
  session: null,
  sessionStatus: "idle",
  sessionError: null,
  addresses: [],
  addressesStatus: "idle",
  addressesError: null,
  isCreatingCheckout: false,
  isUpdatingAddress: false,
  isCreatingAddress: false,
  installmentOptions: null,
  installmentOptionsStatus: "idle",
  checkoutSummary: null,
  isRefreshing: false,
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    resetCheckout: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = null;
      state.session = null;
      state.sessionStatus = "idle";
      state.sessionError = null;
      state.isCreatingCheckout = false;
      state.isUpdatingAddress = false;
      state.isCreatingAddress = false;
      state.installmentOptions = null;
      state.installmentOptionsStatus = "idle";
      state.checkoutSummary = null;
      state.isRefreshing = false;
    },
    resetInstallmentOptions: (state) => {
      state.installmentOptions = null;
      state.installmentOptionsStatus = "idle";
    },
    setCheckoutSummary: (state, action: PayloadAction<CheckoutSummaryItem[]>) => {
      state.checkoutSummary = action.payload;
    },
    clearCheckoutSession: (state) => {
      state.session = null;
      state.sessionStatus = "idle";
      state.sessionError = null;
    },
  },
  extraReducers: (build) => {
    // Get Checkout
    build
      // Sessiz (arka plan) tazeleme `status`'e DOKUNMAZ. Dokunsaydı sayfa
      // CheckoutPageSkeleton'a düşer, kullanıcının seçtiği ödeme yöntemi ve
      // girdiği kart bilgileri ekrandan silinirdi.
      .addCase(getCheckout.pending, (state, action) => {
        if (action.meta.arg?.silent) {
          state.isRefreshing = true;
          return;
        }
        state.status = "loading";
        state.error = null;
      })
      .addCase(getCheckout.fulfilled, (state, action) => {
        state.status = "success";
        state.isRefreshing = false;
        state.data = action.payload.data;
        state.addresses = action.payload.data.addresses;
        state.addressesStatus = "success";
      })
      .addCase(getCheckout.rejected, (state, action) => {
        state.isRefreshing = false;
        // Sessiz tazeleme başarısızsa ekrandaki veri korunur: adres bazlı bir
        // tazelemenin patlaması yüzünden dolu checkout sayfasını hata ekranına
        // çevirmek, kullanıcıyı sepetten başlatmak demek olurdu.
        if (action.meta.arg?.silent) return;
        state.status = "error";
        state.error =
          action.payload ?? "Checkout bilgileri yüklenirken bir hata oluştu.";
      });

    // Create Checkout
    build
      .addCase(createCheckout.pending, (state) => {
        state.isCreatingCheckout = true;
        state.sessionError = null;
      })
      .addCase(createCheckout.fulfilled, (state) => {
        state.isCreatingCheckout = false;
        state.sessionStatus = "success";
      })
      .addCase(
        createCheckout.rejected,
        (state, action: PayloadAction<string | { requiresOtp: true; phone?: string } | undefined>) => {
          state.isCreatingCheckout = false;
          state.sessionStatus = "error";
          state.sessionError =
            typeof action.payload === "string" ? action.payload : "Checkout oluşturulurken bir hata oluştu.";
        },
      );

    // Get Checkout Session
    build
      .addCase(getCheckoutSession.pending, (state) => {
        state.sessionStatus = "loading";
        state.sessionError = null;
      })
      .addCase(
        getCheckoutSession.fulfilled,
        (state, action: PayloadAction<GetCheckoutSessionResponse>) => {
          state.sessionStatus = "success";
          state.session = action.payload.data;
        },
      )
      .addCase(
        getCheckoutSession.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.sessionStatus = "error";
          state.sessionError =
            action.payload ?? "Checkout oturumu yüklenirken bir hata oluştu.";
        },
      );

    // Create Address
    build
      .addCase(createAddress.pending, (state) => {
        state.isCreatingAddress = true;
        state.addressesError = null;
      })
      .addCase(
        createAddress.fulfilled,
        (state, action: PayloadAction<CreateAddressResponse>) => {
          state.isCreatingAddress = false;
          state.addresses.push(action.payload.data.address);
          state.addressesStatus = "success";
        },
      )
      .addCase(
        createAddress.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.isCreatingAddress = false;
          state.addressesError =
            action.payload ?? "Adres eklenirken bir hata oluştu.";
        },
      );

    // Update Address
    build
      .addCase(updateAddress.pending, (state) => {
        state.isUpdatingAddress = true;
        state.addressesError = null;
      })
      .addCase(
        updateAddress.fulfilled,
        (state, action: PayloadAction<UpdateAddressResponse>) => {
          state.isUpdatingAddress = false;
          const index = state.addresses.findIndex(
            (addr) => addr.id === action.payload.data.address.id,
          );
          if (index !== -1) {
            state.addresses[index] = action.payload.data.address;
          }
          state.addressesStatus = "success";
        },
      )
      .addCase(
        updateAddress.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.isUpdatingAddress = false;
          state.addressesError =
            action.payload ?? "Adres güncellenirken bir hata oluştu.";
        },
      );

    // Update Shipping Address
    build
      .addCase(updateShippingAddress.pending, (state) => {
        state.isUpdatingAddress = true;
        state.sessionError = null;
      })
      .addCase(
        updateShippingAddress.fulfilled,
        (state, action: PayloadAction<UpdateShippingAddressResponse>) => {
          state.isUpdatingAddress = false;
          if (state.session) {
            state.session.checkout.shipping_address =
              action.payload.data.checkout.shipping_address;
          }
        },
      )
      .addCase(
        updateShippingAddress.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.isUpdatingAddress = false;
          state.sessionError =
            action.payload ?? "Teslimat adresi güncellenirken bir hata oluştu.";
        },
      );

    // Update Billing Address
    build
      .addCase(updateBillingAddress.pending, (state) => {
        state.isUpdatingAddress = true;
        state.sessionError = null;
      })
      .addCase(
        updateBillingAddress.fulfilled,
        (state, action: PayloadAction<UpdateBillingAddressResponse>) => {
          state.isUpdatingAddress = false;
          if (state.session) {
            state.session.checkout.billing_address =
              action.payload.data.checkout.billing_address;
          }
        },
      )
      .addCase(
        updateBillingAddress.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.isUpdatingAddress = false;
          state.sessionError =
            action.payload ?? "Fatura adresi güncellenirken bir hata oluştu.";
        },
      );

    // Get Installment Options
    build
      .addCase(getInstallmentOptions.pending, (state) => {
        state.installmentOptionsStatus = "loading";
        state.installmentOptions = null;
      })
      .addCase(
        getInstallmentOptions.fulfilled,
        (state, action: PayloadAction<GetInstallmentOptionsResponse>) => {
          state.installmentOptionsStatus = "success";
          state.installmentOptions = action.payload.data;
        },
      )
      .addCase(getInstallmentOptions.rejected, (state) => {
        state.installmentOptionsStatus = "error";
        state.installmentOptions = null;
      });
  },
});

export const { resetCheckout, clearCheckoutSession, resetInstallmentOptions, setCheckoutSummary } = checkoutSlice.actions;

export default checkoutSlice.reducer;